import { useCallback, useEffect, useMemo, useState } from 'react'
import Icon from '../../components/icons.jsx'
import { useApp } from '../../context/AppContext.jsx'
import * as api from '../../db/api.js'
import '../financeiro/contas.css'
import './meu-perfil.css'
import './config-usuarios.css'

const INVITE_STATUS_MAP = {
  active: ['green', 'Ativo'],
  used: ['neutral', 'Utilizado'],
  expired: ['amber', 'Expirado'],
  revoked: ['red', 'Revogado'],
}

const EXPIRY_OPTIONS = [
  { label: 'Sem expiração', value: '' },
  { label: 'Expira em 7 dias', value: '7' },
  { label: 'Expira em 30 dias', value: '30' },
  { label: 'Expira em 90 dias', value: '90' },
]

const MEMBER_ROLE_OPTIONS = [
  {
    value: 'viewer',
    label: 'Visualizador',
    description: 'Perfil limitado: acessa apenas os clientes e módulos atribuídos, respeitando o perfil definido em cada cliente.',
  },
  {
    value: 'manager',
    label: 'Gerente',
    description: 'Perfil operacional limitado: não recebe acesso amplo por si só; depende dos clientes e módulos atribuídos nesta tela.',
  },
  {
    value: 'admin',
    label: 'Administrador',
    description: 'Acesso amplo aos clientes da organização e gestão de usuários, convites e permissões — exceto o dono e outros administradores. Só o dono concede o perfil de administrador.',
  },
  {
    value: 'comum',
    label: 'Membro',
    description: 'Perfil legado de membro comum: funciona como acesso limitado aos clientes e módulos atribuídos.',
  },
]

const CLIENT_ROLE_OPTIONS = [
  { value: 'viewer', label: 'Viewer' },
  { value: 'editor', label: 'Editor' },
  { value: 'admin', label: 'Admin' },
]

const PERMISSION_MODULES = [
  { key: 'ads', label: 'Ads' },
  { key: 'crm', label: 'CRM' },
  { key: 'atendimento', label: 'Atendimento' },
  { key: 'financeiro', label: 'Financeiro' },
  { key: 'projetos', label: 'Projetos' },
  { key: 'seo', label: 'SEO' },
  { key: 'whatsapp', label: 'WhatsApp' },
]

// Regras de gestão (issue #239, espelham o backend em api/invites/router.py):
// owner e admin gerenciam convites e usuários; admin não concede o perfil admin
// e não altera, revoga, restaura ou remove o dono nem outros administradores.
const MANAGER_ROLES = new Set(['owner', 'admin'])

function canManageTarget(isOwner, target) {
  if (isOwner) return true
  return !MANAGER_ROLES.has(target?.member_role)
}

const TABS = [
  { id: 'usuarios', label: 'Usuários', icon: 'users' },
  { id: 'convites', label: 'Convites', icon: 'key' },
  { id: 'permissoes', label: 'Permissões', icon: 'sliders' },
]

function roleLabel(role) {
  if (role === 'owner') return 'Proprietário'
  return MEMBER_ROLE_OPTIONS.find((item) => item.value === role)?.label || role || 'Membro'
}

function roleDescription(role) {
  return MEMBER_ROLE_OPTIONS.find((item) => item.value === role)?.description || 'Perfil de acesso definido pela organização.'
}

function roleTone(role) {
  if (role === 'admin') return 'blue'
  if (role === 'manager') return 'amber'
  if (role === 'owner') return 'black'
  return 'neutral'
}

function defaultModuleState(enabled = true) {
  return Object.fromEntries(PERMISSION_MODULES.map((mod) => [mod.key, enabled]))
}

function fmtDate(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('pt-BR')
}

function userName(user) {
  return user.full_name || user.email || user.user_id
}

function UserIdentity({ person, isSelf }) {
  const initials = (userName(person).match(/[\p{L}\p{N}]+/gu) || ['U']).slice(0, 2).map(part => part[0]).join('').toUpperCase()
  return <div className="us-person">
    <span className="us-avatar" aria-hidden="true">{initials}</span>
    <div className="us-person-text"><div className="fc-desc">{userName(person)}{isSelf && <span className="us-you">Você</span>}</div><div className="fc-desc-sub">{person.email || 'Sem e-mail'}</div></div>
  </div>
}

function countActiveClients(clients = []) {
  return (clients || []).filter((client) => client.is_active !== false).length
}

function inviteClientLabel(invite, totalClients) {
  const count = (invite.client_access || []).length
  if (!count) return totalClients ? 'Todos os clientes' : 'Sem clientes'
  return `${count} cliente${count === 1 ? '' : 's'}`
}

function inviteUsedBy(invite) {
  return invite.used_by_email || invite.used_by_name || invite.used_by || invite.used_by_user_id || '-'
}

function PageEmpty({ icon = 'info', title, desc }) {
  return (
    <div className="fc-card">
      <div className="empty">
        <div className="empty-icon"><Icon n={icon} s={18} /></div>
        {title && <div className="empty-title">{title}</div>}
        {desc && <div className="empty-desc">{desc}</div>}
      </div>
    </div>
  )
}

function RoleHelp({ role }) {
  return (
    <div className="us-role-help">
      <span className="mp-section-icon"><Icon n="info" s={15} /></span>
      <div>
        <div className="fc-desc" style={{ fontSize: 13 }}>{roleLabel(role)}</div>
        <div className="fc-desc-sub" style={{ maxWidth: 720, lineHeight: 1.45 }}>{roleDescription(role)}</div>
      </div>
    </div>
  )
}

function AccessMatrix({ access, setAccess }) {
  const { availableClients } = useApp()

  function patchClient(clientId, patch) {
    setAccess((prev) => ({
      ...prev,
      [clientId]: { ...(prev[clientId] || {}), ...patch },
    }))
  }

  function patchModule(clientId, moduleKey, checked) {
    setAccess((prev) => ({
      ...prev,
      [clientId]: {
        ...(prev[clientId] || {}),
        modules: {
          ...defaultModuleState(true),
          ...(prev[clientId]?.modules || {}),
          [moduleKey]: checked,
        },
      },
    }))
  }

  if (!availableClients?.length) {
    return <PageEmpty icon="briefcase" desc="Crie um cliente antes de configurar acessos." />
  }

  return (
    <div className="fc-card">
      <div className="fc-table-scroll">
        <table className="fc-table" style={{ minWidth: 820 }}>
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Acesso</th>
              <th>Perfil no cliente</th>
              <th>Módulos permitidos</th>
            </tr>
          </thead>
          <tbody>
            {availableClients.map((client) => {
              const cfg = access[client.id] || { selected: true, role: 'viewer', modules: defaultModuleState(true) }
              const disabled = cfg.selected === false
              return (
                <tr key={client.id}>
                  <td>
                    <div className="fc-desc">{client.name}</div>
                    <div className="fc-desc-sub">{client.domain || 'Sem domínio'}</div>
                  </td>
                  <td>
                    <label className={`badge badge-${disabled ? 'neutral' : 'green'}`} style={{ cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={!disabled}
                        onChange={(event) => patchClient(client.id, { selected: event.target.checked })}
                      />
                      {disabled ? 'Sem acesso' : 'Permitido'}
                    </label>
                  </td>
                  <td>
                    <select
                      className="fc-ctrl"
                      value={cfg.role || 'viewer'}
                      disabled={disabled}
                      onChange={(event) => patchClient(client.id, { role: event.target.value })}
                    >
                      {CLIENT_ROLE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {PERMISSION_MODULES.map((mod) => {
                        const checked = (cfg.modules || {})[mod.key] !== false
                        return (
                          <label
                            key={mod.key}
                            className={`badge badge-${checked && !disabled ? 'blue' : 'neutral'}`}
                            style={{ cursor: disabled ? 'default' : 'pointer' }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={disabled}
                              onChange={(event) => patchModule(client.id, mod.key, event.target.checked)}
                            />
                            {mod.label}
                          </label>
                        )
                      })}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function UsuariosTab({ loading, users, currentUserId, isOwner, onEdit, onRevoke, onRestore, onRemove }) {
  if (loading) return <PageEmpty icon="refresh" desc="Carregando usuários..." />

  if (!users.length) {
    return (
      <PageEmpty
        icon="users"
        title="Nenhum usuário vinculado"
        desc="Gere um convite para adicionar alguém à organização."
      />
    )
  }

  return (
    <>
      <div className="fc-bulk" style={{ justifyContent: 'space-between' }}>
        <span className="fc-bulk-count">{users.length} usuário{users.length === 1 ? '' : 's'} na organização</span>
      </div>
      <div className="fc-card">
        <div className="fc-table-scroll">
          <table className="fc-table" style={{ minWidth: 860 }}>
            <thead>
              <tr>
                <th>Usuário</th>
                <th>Perfil</th>
                <th>Clientes</th>
                <th>Status</th>
                <th className="fc-col-acoes">Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const isSelf = user.user_id === currentUserId
                const locked = isSelf || !canManageTarget(isOwner, user)
                return (
                  <tr key={user.user_id}>
                    <td>
                      <UserIdentity person={user} isSelf={user.user_id === currentUserId} />
                    </td>
                    <td><span className={`badge badge-${roleTone(user.member_role)}`}>{roleLabel(user.member_role)}</span></td>
                    <td>{countActiveClients(user.clients)} cliente{countActiveClients(user.clients) === 1 ? '' : 's'}</td>
                    <td>
                      <span className={`badge badge-${user.member_active ? 'green' : 'red'}`}>
                        {user.member_active ? 'Ativo' : 'Revogado'}
                      </span>
                    </td>
                    <td className="fc-col-acoes">
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'nowrap' }}>
                        <button
                          type="button"
                          className="fc-ctrl"
                          onClick={() => onEdit(user)}
                          disabled={locked}
                          title={!isSelf && locked ? 'Só o dono pode alterar o dono ou administradores.' : undefined}
                        >
                          <Icon n="sliders" s={15} /> Permissões
                        </button>
                        {user.member_active && !locked && (
                          <button type="button" className="fc-icon-btn danger" onClick={() => onRevoke(user.user_id)} title="Revogar acesso">
                            <Icon n="trash" s={15} />
                          </button>
                        )}
                        {!user.member_active && !locked && (
                          <>
                            <button type="button" className="fc-icon-btn danger" onClick={() => onRemove(user.user_id)} title="Remover usuário">
                              <Icon n="trash" s={15} />
                            </button>
                            <button type="button" className="fc-icon-btn" onClick={() => onRestore(user.user_id)} title="Liberar acesso novamente">
                              <Icon n="refresh" s={15} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

function ConvitesTab({ loading, invites, totalClients, onCopy, onRevoke }) {
  if (loading) return <PageEmpty icon="refresh" desc="Carregando convites..." />

  return (
    <>
      <div className="fc-bulk" style={{ justifyContent: 'space-between' }}>
        <span className="fc-bulk-count">{invites.length} convite{invites.length === 1 ? '' : 's'} gerado{invites.length === 1 ? '' : 's'}</span>
      </div>

      {!invites.length ? (
        <PageEmpty icon="key" title="Nenhum convite gerado" desc="Crie um código com perfil, clientes e permissões predefinidas." />
      ) : (
        <div className="fc-card">
          <div className="fc-table-scroll">
            <table className="fc-table" style={{ minWidth: 980 }}>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Perfil</th>
                  <th>Clientes</th>
                  <th>Expiração</th>
                  <th>Utilizado por</th>
                  <th>Status</th>
                  <th className="fc-col-acoes">Ações</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((invite) => {
                  const [tone, label] = INVITE_STATUS_MAP[invite.status] || INVITE_STATUS_MAP.revoked
                  return (
                    <tr key={invite.id}>
                      <td>
                        <div className="fc-desc" style={{ fontFamily: 'monospace', letterSpacing: '.04em' }}>
                          {invite.code_hidden ? '••••••••' : invite.invite_code}
                        </div>
                        {invite.code_hidden ? (
                          <div className="fc-desc-sub">Código visível só para o dono</div>
                        ) : null}
                        <div className="fc-desc-sub">Criado em {fmtDate(invite.created_at)}</div>
                      </td>
                      <td><span className={`badge badge-${roleTone(invite.member_role)}`}>{roleLabel(invite.member_role)}</span></td>
                      <td>{inviteClientLabel(invite, totalClients)}</td>
                      <td>{invite.expires_at ? fmtDate(invite.expires_at) : 'Sem expiração'}</td>
                      <td>{invite.status === 'used' ? inviteUsedBy(invite) : '-'}</td>
                      <td><span className={`badge badge-${tone}`}>{label}</span></td>
                      <td className="fc-col-acoes">
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                          {!invite.code_hidden && invite.invite_code && (
                            <button type="button" className="fc-icon-btn" onClick={() => onCopy(invite.invite_code)} title="Copiar código">
                              <Icon n="page" s={15} />
                            </button>
                          )}
                          {invite.status === 'active' && (
                            <button type="button" className="fc-icon-btn danger" onClick={() => onRevoke(invite.id)} title="Revogar convite">
                              <Icon n="trash" s={15} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  )
}

function PermissoesTab({
  loading,
  users,
  currentUserId,
  isOwner,
  roleOptions,
  selectedUser,
  editRole,
  setEditRole,
  editAccess,
  setEditAccess,
  onSelect,
  onSave,
  onCancel,
  onRevoke,
  onRestore,
  onRemove,
}) {
  if (loading) return <PageEmpty icon="refresh" desc="Carregando permissões..." />

  if (!users.length) {
    return <PageEmpty icon="users" title="Nenhum usuário vinculado" desc="As permissões ficam disponíveis depois que um usuário entra na organização." />
  }

  if (!selectedUser) {
    return (
      <>
        <div className="fc-bulk">
          <span className="fc-bulk-count">Escolha um usuário para configurar acesso por cliente e módulo.</span>
        </div>
        <div className="fc-card">
          <div className="fc-table-scroll">
            <table className="fc-table" style={{ minWidth: 760 }}>
              <thead>
                <tr>
                  <th>Usuário</th>
                  <th>Perfil atual</th>
                  <th>Clientes</th>
                  <th className="fc-col-acoes">Ações</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.user_id}>
                    <td>
                      <UserIdentity person={user} isSelf={user.user_id === currentUserId} />
                    </td>
                    <td><span className={`badge badge-${roleTone(user.member_role)}`}>{roleLabel(user.member_role)}</span></td>
                    <td>{countActiveClients(user.clients)} cliente{countActiveClients(user.clients) === 1 ? '' : 's'}</td>
                    <td className="fc-col-acoes">
                      <button
                        type="button"
                        className="fc-ctrl"
                        onClick={() => onSelect(user)}
                        disabled={user.user_id === currentUserId || !canManageTarget(isOwner, user)}
                      >
                        <Icon n="sliders" s={15} /> Configurar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    )
  }

  const isSelf = selectedUser.user_id === currentUserId
  const locked = isSelf || !canManageTarget(isOwner, selectedUser)

  return (
    <>
      <div className="fc-bulk" style={{ justifyContent: 'space-between' }}>
        <div>
          <UserIdentity person={selectedUser} isSelf={isSelf} />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button type="button" className="fc-ctrl" onClick={onCancel}>Cancelar</button>
          {selectedUser.member_active && !locked && (
            <button type="button" className="fc-ctrl" onClick={() => onRevoke(selectedUser.user_id)}>
              <Icon n="trash" s={15} /> Revogar
            </button>
          )}
          {!selectedUser.member_active && !locked && (
            <>
              <button type="button" className="fc-icon-btn danger" onClick={() => onRemove(selectedUser.user_id)} title="Remover usuário">
                <Icon n="trash" s={15} />
              </button>
              <button type="button" className="fc-icon-btn" onClick={() => onRestore(selectedUser.user_id)} title="Liberar acesso novamente">
                <Icon n="refresh" s={15} />
              </button>
            </>
          )}
          <button type="button" className="fc-ctrl primary" onClick={() => onSave(selectedUser.user_id)} disabled={locked}>
            <Icon n="check" s={15} /> {selectedUser.member_active ? 'Salvar' : 'Salvar e adicionar'}
          </button>
        </div>
      </div>

      <div className="fc-card" style={{ marginBottom: 12 }}>
        <div style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span className="fc-bulk-count">Perfil na organização</span>
          <select className="fc-ctrl" value={editRole} onChange={(event) => setEditRole(event.target.value)} disabled={locked}>
            {roleOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </div>
        <RoleHelp role={editRole} />
      </div>

      <h3 className="us-matrix-title">Acesso por cliente</h3>
      <AccessMatrix access={editAccess} setAccess={setEditAccess} />
    </>
  )
}

function InviteModal({ open, creating, roleOptions, expiry, setExpiry, memberRole, setMemberRole, clientAccess, setClientAccess, onClose, onCreate }) {
  if (!open) return null

  return (
    <div className="fc-modal-overlay" onMouseDown={onClose}>
      <div className="fc-modal us-invite-modal" style={{ width: 960 }} role="dialog" aria-modal="true" aria-labelledby="us-invite-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="fc-modal-head" style={{ justifyContent: 'space-between' }}>
          <div>
            <h2 id="us-invite-title">Novo convite</h2>
            <div className="fc-desc-sub">Defina perfil, clientes e módulos antes de gerar o código.</div>
          </div>
          <button type="button" className="fc-icon-btn" onClick={onClose} title="Fechar">
            <Icon n="x" s={15} />
          </button>
        </div>
        <div className="fc-modal-body">
          <div className="us-invite-fields">
            <label className="us-select-field"><span>Perfil na organização</span>
            <select className="fc-ctrl" value={memberRole} onChange={(event) => setMemberRole(event.target.value)} disabled={creating}>
              {roleOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            </label><label className="us-select-field"><span>Validade do convite</span>
            <select className="fc-ctrl" value={expiry} onChange={(event) => setExpiry(event.target.value)} disabled={creating}>
              {EXPIRY_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select></label>
          </div>
          <div className="fc-card" style={{ marginBottom: 12 }}>
            <RoleHelp role={memberRole} />
          </div>
          <h3 className="us-matrix-title">Acesso por cliente</h3>
          <AccessMatrix access={clientAccess} setAccess={setClientAccess} />
        </div>
        <div className="fc-modal-foot">
          <button type="button" className="fc-ctrl" onClick={onClose} disabled={creating}>Cancelar</button>
          <button type="button" className="fc-ctrl primary" onClick={onCreate} disabled={creating}>
            {creating ? 'Gerando...' : 'Gerar convite'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function ConfigUsuarios() {
  const { toast, memberships, availableClients, user } = useApp()
  const activeMemberships = (memberships || []).filter((m) => m.is_active !== false)
  const isOwner = activeMemberships.some((m) => m.role === 'owner')
  const canManage = activeMemberships.some((m) => MANAGER_ROLES.has(m.role))
  // Só o dono concede o perfil admin (convite e edição).
  const roleOptions = useMemo(
    () => (isOwner ? MEMBER_ROLE_OPTIONS : MEMBER_ROLE_OPTIONS.filter((option) => option.value !== 'admin')),
    [isOwner]
  )

  const [activeTab, setActiveTab] = useState('usuarios')
  const [inviteModalOpen, setInviteModalOpen] = useState(false)
  const [invites, setInvites] = useState([])
  const [orgUsers, setOrgUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [expiry, setExpiry] = useState('')
  const [memberRole, setMemberRole] = useState('viewer')
  const [clientAccess, setClientAccess] = useState({})
  const [editingUserId, setEditingUserId] = useState(null)
  const [editRole, setEditRole] = useState('viewer')
  const [editAccess, setEditAccess] = useState({})
  const [error, setError] = useState('')

  const buildAccess = useCallback((grants = [], defaultSelected = true) => {
    const byClient = new Map((grants || []).map((grant) => [grant.client_id, grant]))
    const next = {}
    for (const client of availableClients || []) {
      const grant = byClient.get(client.id)
      const modules = grant?.permissions?.modules
      next[client.id] = {
        selected: grant ? grant.is_active !== false : defaultSelected,
        role: grant?.role || 'viewer',
        modules: modules && Object.keys(modules).length
          ? Object.fromEntries(PERMISSION_MODULES.map((mod) => [mod.key, !!modules[mod.key]]))
          : defaultModuleState(true),
      }
    }
    return next
  }, [availableClients])

  useEffect(() => {
    if (!availableClients?.length) return
    setClientAccess((current) => (Object.keys(current).length ? current : buildAccess()))
  }, [availableClients, buildAccess])

  const load = useCallback(async () => {
    if (!canManage) return
    try {
      setLoading(true)
      setError('')
      const [inviteData, usersData] = await Promise.all([
        api.listInvites(),
        api.listOrganizationUsers(),
      ])
      setInvites(Array.isArray(inviteData) ? inviteData : [])
      setOrgUsers(Array.isArray(usersData) ? usersData : [])
    } catch (err) {
      console.error('[users] erro:', err)
      setError(err.message || 'Não foi possível listar usuários e convites.')
    } finally {
      setLoading(false)
    }
  }, [canManage])

  useEffect(() => {
    load()
  }, [load])

  const activeInvites = useMemo(() => invites.filter((item) => item.status === 'active').length, [invites])
  const selectedUser = useMemo(() => orgUsers.find((user) => user.user_id === editingUserId) || null, [orgUsers, editingUserId])

  function buildPayload(access) {
    return Object.entries(access)
      .filter(([, cfg]) => cfg.selected !== false)
      .map(([clientId, cfg]) => ({
        client_id: clientId,
        role: cfg.role || 'viewer',
        permissions: {
          modules: Object.fromEntries(
            PERMISSION_MODULES
              .filter((mod) => (cfg.modules || {})[mod.key] !== false)
              .map((mod) => [mod.key, cfg.role || 'viewer'])
          ),
        },
      }))
  }

  async function handleCreate() {
    const clients = buildPayload(clientAccess)
    if (!clients.length) {
      toast('Selecione ao menos um cliente.', 'error')
      return
    }
    try {
      setCreating(true)
      const invite = await api.createInvite({
        expiresInDays: expiry ? Number(expiry) : null,
        memberRole,
        clients,
      })
      setInvites((prev) => [invite, ...prev])
      setInviteModalOpen(false)
      toast('Convite gerado.', 'success')
    } catch (err) {
      toast(err.message || 'Falha ao gerar convite.', 'error')
    } finally {
      setCreating(false)
    }
  }

  async function handleRevoke(inviteId) {
    if (!window.confirm('Revogar este convite? Ele nao podera mais ser usado.')) return
    try {
      const updated = await api.revokeInvite(inviteId)
      setInvites((prev) => prev.map((item) => (item.id === inviteId ? updated : item)))
      toast('Convite revogado.', 'success')
    } catch (err) {
      toast(err.message || 'Falha ao revogar convite.', 'error')
    }
  }

  async function handleCopy(code) {
    try {
      await navigator.clipboard.writeText(code)
      toast('Código copiado.', 'success')
    } catch {
      toast('Não foi possível copiar.', 'error')
    }
  }

  function startEdit(user) {
    setEditingUserId(user.user_id)
    setEditRole(user.member_role || 'viewer')
    setEditAccess(buildAccess(user.clients || [], false))
    setActiveTab('permissoes')
  }

  async function saveEdit(userId) {
    const clients = buildPayload(editAccess)
    if (!clients.length) {
      toast('Selecione ao menos um cliente ou revogue o acesso.', 'error')
      return
    }
    try {
      await api.updateOrganizationUserAccess(userId, { member_role: editRole, clients })
      toast('Acesso atualizado.', 'success')
      setEditingUserId(null)
      await load()
    } catch (err) {
      toast(err.message || 'Falha ao atualizar acesso.', 'error')
    }
  }

  async function revokeUser(userId) {
    if (!window.confirm('Revogar completamente o acesso deste usuário à organização?')) return
    try {
      await api.revokeOrganizationUserAccess(userId)
      toast('Acesso revogado.', 'success')
      setEditingUserId((current) => (current === userId ? null : current))
      await load()
    } catch (err) {
      toast(err.message || 'Falha ao revogar acesso.', 'error')
    }
  }

  async function restoreUser(userId) {
    if (!window.confirm('Adicionar este usuário novamente à organização?')) return
    try {
      await api.restoreOrganizationUserAccess(userId)
      toast('Usuário adicionado novamente.', 'success')
      await load()
    } catch (err) {
      toast(err.message || 'Falha ao adicionar usuário novamente.', 'error')
    }
  }

  async function removeUser(userId) {
    if (!window.confirm('Remover este usuário da organização? Os vínculos de acesso desta organização serão apagados.')) return
    try {
      await api.removeOrganizationUser(userId)
      toast('Usuário removido da organização.', 'success')
      setEditingUserId((current) => (current === userId ? null : current))
      await load()
    } catch (err) {
      toast(err.message || 'Falha ao remover usuário.', 'error')
    }
  }

  function openInviteModal() {
    setClientAccess((current) => (Object.keys(current).length ? current : buildAccess()))
    setInviteModalOpen(true)
    setActiveTab('convites')
  }

  if (!canManage) {
    return (
      <div className="mp-page us-page"><div className="mp-card">
        <div className="empty">
          <div className="empty-icon"><Icon n="key" s={18} /></div>
          <div className="empty-desc">Apenas o dono ou um administrador da organização pode gerenciar usuários e convites.</div>
        </div>
      </div></div>
    )
  }

  return (
    <div className="mp-page us-page">
      <header className="mp-card us-page-header">
        <div className="us-heading">
          <span className="us-heading-icon"><Icon n="users" s={21} /></span>
          <div><h1>Usuários e acessos</h1><p>Gerencie as pessoas, os convites e as permissões da organização.</p></div>
        </div>
        <button type="button" className="mp-button mp-button-primary" onClick={openInviteModal}><Icon n="plus" s={14} />Gerar convite</button>
      </header>
      <div className="mp-card us-workspace">
      <div className="fc-tabs" role="tablist" aria-label="Gestão de usuários">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`fc-tab${activeTab === tab.id ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            role="tab"
            aria-selected={activeTab === tab.id}
          >
            <Icon n={tab.icon} s={15} />
            {tab.label}
            {tab.id === 'usuarios' && <span className="fc-tab-count">{orgUsers.length}</span>}
            {tab.id === 'convites' && <span className="fc-tab-count">{activeInvites}</span>}
          </button>
        ))}
      </div>

      <div className="us-tab-content">
      {error && <div className="auth-error" role="alert" style={{ marginBottom: 12 }}>{error}</div>}

      {activeTab === 'usuarios' && (
        <UsuariosTab
          loading={loading}
          users={orgUsers}
          currentUserId={user?.id}
          isOwner={isOwner}
          onEdit={startEdit}
          onRevoke={revokeUser}
          onRestore={restoreUser}
          onRemove={removeUser}
        />
      )}

      {activeTab === 'convites' && (
        <ConvitesTab
          loading={loading}
          invites={invites}
          totalClients={availableClients?.length || 0}
          onCopy={handleCopy}
          onRevoke={handleRevoke}
        />
      )}

      {activeTab === 'permissoes' && (
        <PermissoesTab
          loading={loading}
          users={orgUsers}
          currentUserId={user?.id}
          isOwner={isOwner}
          roleOptions={roleOptions}
          selectedUser={selectedUser}
          editRole={editRole}
          setEditRole={setEditRole}
          editAccess={editAccess}
          setEditAccess={setEditAccess}
          onSelect={startEdit}
          onSave={saveEdit}
          onCancel={() => setEditingUserId(null)}
          onRevoke={revokeUser}
          onRestore={restoreUser}
          onRemove={removeUser}
        />
      )}

      </div>
      </div>
      <InviteModal
        open={inviteModalOpen}
        creating={creating}
        roleOptions={roleOptions}
        expiry={expiry}
        setExpiry={setExpiry}
        memberRole={memberRole}
        setMemberRole={setMemberRole}
        clientAccess={clientAccess}
        setClientAccess={setClientAccess}
        onClose={() => setInviteModalOpen(false)}
        onCreate={handleCreate}
      />
    </div>
  )
}
